import React, { useState, useMemo, useRef, useId } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  ColumnFiltersState,
} from '@tanstack/react-table';

import { useNavigate } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  SearchInput,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  ResultCountContainer,
  ResultCount,
  FilterBarRowSection,
  FilterBarRow,
  TableTabList,
  HeaderSortingIndicator,
} from '../Table';
import FormattedCell from '../FormattedCell';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { PriorAuthTableRow } from '../../api';
import { TableMultiselectFilter } from '../TableDropdownFilter/TableDropdownFilter';
import {
  type MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import {
  sortOfficeNames,
  getInitialMultiselectFilterValueFromCookie,
  getInitialTableColumnFilterValueFromCookie,
  getDropdownInitialValueFromSearchParams,
  formatStandardDateLocaleString,
  updateTableColumnFilterValueCookie,
} from '../../utils';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import styled from 'styled-components';
import { focusVisibleStyles } from 'design-system/src/sharedStyles';
import { AuthContext } from '../../auth';

import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from '../SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';

const columnHelper = createColumnHelper<PriorAuthTableRow>();

type PriorAuthTabValue = 'open' | 'dismissed';

interface PriorAuthTableProps {
  data: PriorAuthTableRow[];
  onDismiss: (priorAuthId: string) => void;
  onRestore: (priorAuthId: string) => void;
}

const OuterTableContainer = styled.div`
  overflow-y: visible;
  height: 100%;
`;

const DismissalButton = styled.a`
  color: var(--dark-gray);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0);
  transition: text-decoration-color 0.3s;
  &:hover {
    text-decoration-color: var(--dark-gray);
  }
  ${focusVisibleStyles}
`;

const DismissedRow = styled(ClickableRow)`
  opacity: 0.5;
  transition: opacity 0.3s;
`;

const priorAuthTableMultiSelectFilter = (row: Row<PriorAuthTableRow>, columnId: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

// prescriber column needs a special filter function because it's in a nested
// object
const prescriberMultiSelectFilter = (row: Row<PriorAuthTableRow>, _: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  return filterValue.includes(row.original.rx.prescriber_name || '');
};

const getDefaultNullStateType = (selectedTab: PriorAuthTabValue) => {
  if (selectedTab === 'dismissed') {
    return TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_DISMISSED;
  } else {
    return TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_OPEN;
  }
};

const DismissCell: React.FC<{
  dismissed: boolean;
  onDismiss: (priorAuthId: string) => void;
  onRestore: (priorAuthId: string) => void;
  priorAuthId: string;
}> = ({ dismissed, onDismiss, onRestore, priorAuthId }) => {
  if (!dismissed)
    return (
      <DismissalButton
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(priorAuthId);
        }}
      >
        Dismiss
      </DismissalButton>
    );
  else
    return (
      <DismissalButton
        onClick={(e) => {
          e.stopPropagation();
          onRestore(priorAuthId);
        }}
      >
        Restore
      </DismissalButton>
    );
};

const PriorAuthTable: React.FC<PriorAuthTableProps> = ({ data, onDismiss, onRestore }) => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<PriorAuthTabValue>('open');
  const tabListId = useId();
  const panelId = useId();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState('');

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    {
      id: 'office_name',
      value: getInitialTableColumnFilterValueFromCookie('prior_auth', 'office_name', userEmail),
    },
    {
      id: 'prescriber_name',
      value: defaultPrescriberNames || [],
    },
  ]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const filteredData = useMemo(
    () => data.filter((row) => (selectedTab === 'dismissed' ? row.pa.dismissed : !row.pa.dismissed)),
    [data, selectedTab],
  );

  const priorAuthPatientSortingFn = (a: Row<PriorAuthTableRow>, b: Row<PriorAuthTableRow>, columnId: string) => {
    const aValue = a.getValue(columnId) as { name: string; dob: string };
    const bValue = b.getValue(columnId) as { name: string; dob: string };
    return bValue.name.localeCompare(aValue.name);
  };

  const priorAuthDrugSortingFn = (a: Row<PriorAuthTableRow>, b: Row<PriorAuthTableRow>, columnId: string) => {
    const aValue = a.getValue(columnId) as { name: string; dateReceived: string; dateExpires: string };
    const bValue = b.getValue(columnId) as { name: string; dateReceived: string; dateExpires: string };
    return bValue.name.localeCompare(aValue.name);
  };

  const columns = [
    columnHelper.accessor(
      (row) => {
        return { name: row.rx.patient_name, dob: row.formatted_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: priorAuthPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.rx.id}
            title={info.getValue().name}
            subtitle={`DOB: ${info.getValue().dob}`}
          />
        ),
      },
    ),
    columnHelper.accessor(
      (row) => {
        return {
          name: row.drug_name,
          dateReceived: row.formatted_prescription_recieved_at,
          dateExpires: row.formatted_approval_until,
        };
      },
      {
        header: 'Drug',
        sortingFn: priorAuthDrugSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.rx.id}
            // TODO: include both drug name and description (currently has description only)
            title={info.getValue().name}
            subtitle={`Prescribed ${formatStandardDateLocaleString(info.getValue().dateReceived)}`}
            customBody={
              info.getValue().dateExpires ? (
                <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--red)" weight={600}>
                  Expires {formatStandardDateLocaleString(info.getValue().dateExpires)}
                </Typography>
              ) : undefined
            }
          />
        ),
      },
    ),
    columnHelper.accessor('rx.prescriber_name', {
      header: 'Prescriber',
      id: 'prescriber_name',
      filterFn: prescriberMultiSelectFilter,
    }),
    columnHelper.accessor('pa.dismissed', {
      header: '',
      enableGlobalFilter: false,
      enableSorting: false,
      cell: (info) => (
        <DismissCell
          dismissed={info.getValue()}
          onDismiss={onDismiss}
          onRestore={onRestore}
          priorAuthId={info.row.original.pa.id}
        />
      ),
    }),

    // this column is only used for filtering, not displaying
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: priorAuthTableMultiSelectFilter,
    }),
  ];

  const table = useReactTable({
    data: filteredData,
    state: {
      columnFilters,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableSorting: true,
    enableGlobalFilter: true,
    // Explicitly define the global filter function so that it works despite the formatted cells
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;

      const searchTerm = filterValue.toLowerCase();

      if (row.original.rx?.patient_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.rx?.prescriber_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.drug_name?.toLowerCase().includes(searchTerm)) return true;

      const dateReceivedStr = formatStandardDateLocaleString(row.original.formatted_prescription_recieved_at);
      if (dateReceivedStr.toLowerCase().includes(searchTerm)) return true;

      const dateExpiresStr = formatStandardDateLocaleString(row.original.formatted_approval_until);
      if (dateExpiresStr.toLowerCase().includes(searchTerm)) return true;

      if (row.original.formatted_dob.toLowerCase().includes(searchTerm)) return true;

      return false;
    },
    initialState: { columnVisibility: { office_name: false } },
  });

  const nullStateType = useTableNullState({
    columnFilters,
    table,
    filterIds: ['office_name', 'prescriber_name'],
    defaultNullStateType: getDefaultNullStateType(selectedTab),
    globalFilter: searchValue,
  });

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters
    setColumnFilters([]);

    // Clear global filter and search input
    table.setGlobalFilter('');
    setSearchValue('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], 'prior_auth', 'office_name', userEmail);

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
  }, [table, userEmail]);

  return (
    <>
      <OuterTableContainer>
        <TableContainer>
          <FilterBar>
            <FilterBarRow>
              <TableTabList
                tabs={[
                  { label: 'Open', value: 'open' },
                  { label: 'Dismissed', value: 'dismissed' },
                ]}
                selectedValue={selectedTab}
                onSelect={(value) => setSelectedTab(value as PriorAuthTabValue)}
                tabListId={tabListId}
                tabPanelId={panelId}
                ariaLabel="Filter prior authorizations by status"
              />
            </FilterBarRow>

            <FilterBarRow $reorderOnSmall>
              <SearchInput
                label="Search by patient name or DOB"
                hideLabel
                value={searchValue}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value;
                  setSearchValue(value);
                  table.setGlobalFilter(value);
                }}
              />
              <TableMultiselectFilter
                options={filterOptions?.offices || []}
                label={'Office'}
                initialValue={getInitialMultiselectFilterValueFromCookie('prior_auth', 'office_name', userEmail)}
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedValuesList = value.map((valueItem) => valueItem.value);
                  table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                  updateTableColumnFilterValueCookie(value, 'prior_auth', 'office_name', userEmail);
                }}
                sortFn={sortOfficeNames}
                ref={officeFilterRef}
              />
              <TableMultiselectFilter
                options={filterOptions?.prescribers || []}
                label={'Prescriber'}
                initialValue={
                  getDropdownInitialValueFromSearchParams(
                    authContext.user?.default_filters,
                    '',
                    'providers',
                    filterOptions?.prescribers,
                  ) as MultiselectAutocompleteOption[]
                }
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedNamesList = value.map((valueItem) => valueItem.description);
                  table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                }}
                ref={prescriberFilterRef}
              />
              <SaveFiltersButton $visible={showSaveButton} onClick={() => setShowSaveModal(true)} />

              <FilterBarRowSection>
                <ResultCountContainer>
                  <ResultCount>
                    {table.getRowModel().rows?.length} {table.getRowModel().rows?.length === 1 ? 'result' : 'results'}
                  </ResultCount>
                </ResultCountContainer>
              </FilterBarRowSection>
            </FilterBarRow>
          </FilterBar>
          <Table role="tabpanel" id={panelId} aria-labelledby={tabListId}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHeader
                        id={header.id}
                        key={header.id}
                        style={{ width: '150px' }}
                        onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                        $sortable={header.column.getCanSort()}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        <HeaderSortingIndicator
                          isSorted={header.column.getIsSorted()}
                          sortDirection={header.column.getIsSorted()}
                        />
                      </TableHeader>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const RowComponent = row.original.pa.dismissed ? DismissedRow : ClickableRow;
                  return (
                    <RowComponent
                      key={row.id}
                      onClick={() => navigate(`/rxs/${row.original.rx.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/rxs/${row.original.rx.id}`);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </RowComponent>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan={columns.length}>
                    <TableNullState nullStateType={nullStateType} onClearFilters={clearAllFilters} />
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      </OuterTableContainer>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </>
  );
};

export default PriorAuthTable;
